"use client";

import { submitPost } from "deso-protocol";
import type { MouseEventHandler } from "react";
import { use, useCallback } from "react";
import { LuEyeOff } from "react-icons/lu";

import { waitForTxn } from "@/backend/api-helpers";
import { useConfirm } from "@/components/ui/confirm";
import { toast } from "@/components/ui/use-toast";
import type { PostType } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

export const HidePostAction = ({
  className,
  onClick,
  setLoading,
  post,
  updatePost,
  onHidden,
  repost = false,
}: {
  setLoading: (x: boolean) => void;
  post: PostType;
  updatePost?: (hash: string, updatedPost: PostType) => void;
  onHidden?: () => void;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  repost?: boolean;
}) => {
  const postHash = post.postHash as string;
  const { currentUserPublicKey } = use(UserContext);
  const { confirm } = useConfirm();

  const onHideClick = useCallback(async () => {
    const confirmed = await confirm({
      title: `Are you sure you want to hide this ${repost ? "repost" : "post"}?`,
      primaryCTA: repost ? "Hide Repost" : "Hide Post",
      type: "warning",
    });

    if (!confirmed) return;

    try {
      setLoading(true);

      updatePost?.(postHash, {
        ...post,
        isHidden: true,
      });

      const data = await submitPost({
        BodyObj: {
          Body: post.body ?? "",
          ImageURLs: post.extraData?.ImageURLs ?? [],
          VideoURLs: post.extraData?.VideoURLs ?? [],
        },
        RepostedPostHashHex: post.repostedPostHash ?? undefined,
        PostHashHexToModify: postHash,
        UpdaterPublicKeyBase58Check: currentUserPublicKey,
        IsHidden: true,
        PostExtraData: post.extraData ?? {},
      });

      if (!data.submittedTransactionResponse?.TxnHashHex) {
        throw new Error("Transaction was not constructed. Please try again.");
      }

      await waitForTxn({
        transactionHash: data.submittedTransactionResponse?.TxnHashHex,
      });
      onHidden?.();

      toast({
        title: "Success",
        description: repost ? "Repost is hidden" : "Post is hidden",
        variant: "success",
      });
    } catch {
      updatePost?.(postHash, {
        ...post,
        isHidden: false,
      });

      toast({
        title: "Error",
        description: "An error occurred while hiding content",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [
    confirm,
    repost,
    setLoading,
    updatePost,
    postHash,
    post,
    currentUserPublicKey,
    onHidden,
  ]);

  return (
    <div
      className={cn(
        `
        flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm
        text-foreground
        hover:bg-popover-foreground hover:text-primary
      `,
        className,
      )}
      onClick={(event) => {
        onClick?.(event);

        if (!event.defaultPrevented) {
          void onHideClick();
        }
      }}
      role="button"
    >
      <LuEyeOff className="size-4 text-muted" />
      <span>Hide {repost ? "Repost" : "Post"}</span>
    </div>
  );
};
